import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate, useParams, Link, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { PRESSURE_PRESETS } from "@/lib/constants";
import { ArrowLeft, Save, Lock, Copy, Unlock } from "lucide-react";
import { toast } from "sonner";
import AuditLog from "@/components/AuditLog";
import PhotosSection from "@/components/PhotosSection";
import { useRole } from "@/lib/roles";
import { useAuth } from "@/contexts/AuthContext";
import { JobVesselSection } from "@/components/test-form/JobVesselSection";
import { TestParamsSection } from "@/components/test-form/TestParamsSection";
import { WeldersSection } from "@/components/test-form/WeldersSection";
import { FailurePointsSection } from "@/components/test-form/FailurePointsSection";
import { NotesSection } from "@/components/test-form/NotesSection";
import { useInlineSave } from "@/components/test-form/useInlineSave";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";

const todayIso = () => new Date().toISOString().slice(0, 10);

const BLANK = {
  job_number: "",
  vessel_type: "",
  vessel_number: "",
  test_result: "",
  test_pressure: "",
  test_medium: "",
  test_person_id: "",
  test_date: "",
  failure_points: [],
  welder1_id: "",
  welder2_id: "",
  witness_tested_by_nobo: false,
  blown_out_employee_id: "",
  blown_out_date: "",
  notes: "",
};

// Defaults applied only to brand-new records (not edits, not re-test clones).
const NEW_DEFAULTS = {
  vessel_type: "Plate",
  test_result: "Pending",
  test_pressure: "35 bar",
  test_medium: "Water",
  test_date: todayIso(),
  witness_tested_by_nobo: false,
};

// Robust employee filters: case-insensitive role match, treat missing `active` as
// active (only exclude when explicitly false), and always keep the currently-saved
// person/welder selectable. The certificate resolves names by ID with no filter,
// so this keeps the form in sync with what the certificate/table already shows.
const roleIn = (e, roles) => roles.includes((e.role || "").toString().toLowerCase());
const notDeactivated = (e) => e.active !== false;
const withSelected = (list, id, employees) => {
  if (!id || list.some((e) => e.id === id)) return list;
  const found = employees.find((e) => e.id === id);
  return found ? [...list, found] : list;
};

export default function TestForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { canWrite, isAdmin, isEmployee } = useRole();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const cloneFrom = searchParams.get("clone_from");

  const [form, setForm] = useState(() =>
    isEdit || cloneFrom ? BLANK : { ...BLANK, ...NEW_DEFAULTS }
  );
  const [orders, setOrders] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [customPressure, setCustomPressure] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [locked, setLocked] = useState(false);
  const [lockedMeta, setLockedMeta] = useState(null); // { blown_out_date, blown_out_name }
  // Pending lock confirmation: { employeeId, date, assumed } — drives the confirm modal.
  const [pendingLock, setPendingLock] = useState(null);
  const [autoVesselNumber, setAutoVesselNumber] = useState(false);
  // After a fail-safe pre-save (photo added to a brand-new record) we hold the
  // new record id here so the rest of the form behaves like an edit.
  const [savedId, setSavedId] = useState(null);
  const [hydrated, setHydrated] = useState(false);
  const effectiveId = id || savedId;

  // ---- Field-level inline save (edit mode only) ----
  // committed holds the last server-confirmed value per field, used to revert on
  // a failed inline PATCH. dirtyRef tracks unsaved create-flow changes.
  const committed = useRef({});
  const dirtyRef = useRef(false);
  const handleRevert = useCallback(
    (key, value) => setForm((f) => ({ ...f, [key]: value })),
    [],
  );
  // `hydrated` gate is CRITICAL: it keeps inline-save inert until the record has
  // fully loaded and `committed` is populated. Without it, the controlled inputs
  // flipping from BLANK -> loaded during mount could fire a destructive PATCH.
  const inlineEnabled = isEdit && !locked && hydrated;
  const { status: saveStatus, errors: saveErrors, scheduleSave } = useInlineSave({
    recordId: id,
    enabled: inlineEnabled,
    committedRef: committed,
    onRevert: handleRevert,
  });
  // Per-field props for the section <Field> components (indicator + inline error).
  // Returns {} in create mode so nothing renders there.
  const fp = useCallback(
    (key) => (isEdit ? { fieldKey: key, state: saveStatus[key], error: saveErrors[key] } : {}),
    [isEdit, saveStatus, saveErrors],
  );

  useEffect(() => {
    // Load each list independently — one failing request must NOT blank the others
    // (previously a single failure left Job/Test Person/Welders all empty).
    api.get("/orders")
      .then((o) => setOrders(o.data))
      .catch((e) => { console.error("orders load failed", e); toast.error("Could not load Job / Freezer numbers."); });
    api.get("/employees")
      .then((e) => setEmployees(e.data))
      .catch((e) => { console.error("employees load failed", e); toast.error("Could not load employees."); });
    api.get("/settings")
      .then((s) => setAutoVesselNumber(!!s.data?.auto_vessel_number))
      .catch((e) => console.error("settings load failed", e));
  }, []);

  // Dirty-state navigation guard — create flow only. Warns before tab close/refresh
  // when there are unsaved changes. Edit mode auto-saves, so no guard is needed there.
  useEffect(() => {
    if (isEdit) return undefined;
    const handler = (e) => {
      if (!dirtyRef.current) return undefined;
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isEdit]);

  // Vessel number is required EXCEPT on brand-new records when the server will auto-assign it.
  const vesselNumberOptional = !isEdit && autoVesselNumber;

  // Auto-fill Test Person with the logged-in user's matching employee record, unless admin.
  // Only runs for fresh records (not edits, not re-test clones — clone flow owns its own defaults).
  useEffect(() => {
    if (isEdit || cloneFrom) return;
    if (!user || user.role === "admin" || user.role === "super-admin") return;
    if (!employees.length) return;
    if (form.test_person_id) return;
    const match = employees.find(
      (e) =>
        e.active &&
        ["tester", "both"].includes(e.role) &&
        e.name.trim().toLowerCase() === (user.name || "").trim().toLowerCase()
    );
    // eslint-disable-next-line
    if (match) setForm((f) => ({ ...f, test_person_id: match.id }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employees, user, isEdit, cloneFrom]);

  useEffect(() => {
    if (isEdit) {
      api.get(`/tests/${id}`).then((res) => {
        const d = res.data;
        const loaded = {
          ...BLANK,
          ...d,
          vessel_number: d.vessel_number ?? "",
          failure_points: d.failure_points || [],
        };
        setForm(loaded);
        // Snapshot server-confirmed values so a failed inline PATCH can revert.
        committed.current = { ...loaded };
        // Enable inline-save ONLY now that form + committed are populated.
        setHydrated(true);
        if (d.test_pressure && !PRESSURE_PRESETS.includes(d.test_pressure)) {
          setCustomPressure(true);
        }
        if (d.blown_out_employee_id) {
          setLocked(true);
          setLockedMeta({
            blown_out_date: d.blown_out_date,
          });
        }
      });
    } else if (cloneFrom) {
      // Pre-fill a fresh re-test from a locked record.
      api.get(`/tests/${cloneFrom}`).then((res) => {
        const d = res.data;
        setForm({
          ...BLANK,
          ...NEW_DEFAULTS,
          job_number: d.job_number || "",
          vessel_type: d.vessel_type || NEW_DEFAULTS.vessel_type,
          vessel_number: d.vessel_number ?? "",
          welder1_id: d.welder1_id || "",
          welder2_id: d.welder2_id || "",
          test_medium: d.test_medium || NEW_DEFAULTS.test_medium,
          test_pressure: d.test_pressure || NEW_DEFAULTS.test_pressure,
          notes: `Re-test after repair/replacement of JST-${d.id.slice(0, 8).toUpperCase()}.`,
        });
        if (d.test_pressure && !PRESSURE_PRESETS.includes(d.test_pressure)) {
          setCustomPressure(true);
        }
      });
    }
  }, [id, isEdit, cloneFrom]);

  const update = (patch) => {
    setForm((f) => ({ ...f, ...patch }));
    if (inlineEnabled) {
      Object.entries(patch).forEach(([k, v]) => scheduleSave(k, v));
    } else if (!isEdit) {
      dirtyRef.current = true;
    }
  };

  const toggleFailurePoint = (code) => {
    const has = form.failure_points.includes(code);
    const next = has
      ? form.failure_points.filter((c) => c !== code)
      : [...form.failure_points, code];
    setForm((f) => ({ ...f, failure_points: next }));
    if (inlineEnabled) {
      scheduleSave("failure_points", next);
    } else if (!isEdit) {
      dirtyRef.current = true;
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    // Edit mode saves field-by-field inline — there is no whole-form submit.
    if (isEdit) return;
    if (locked) return;
    setError("");
    const errMsg = validateForm();
    if (errMsg) {
      setError(errMsg);
      return;
    }

    setSubmitting(true);
    try {
      const payload = buildPayload();
      if (effectiveId) {
        await api.put(`/tests/${effectiveId}`, payload);
        toast.success("Test updated");
      } else {
        await api.post("/tests", payload);
        toast.success("Test created");
      }
      dirtyRef.current = false;
      navigate("/tests");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  // Shared client-side required-field validation. Returns an error string, or "".
  const validateForm = () => {
    const isFreezerAssembly = form.vessel_type === "Freezer Assembly";
    const required = [
      ["job_number", "Job / Freezer number"],
      ["vessel_type", "Vessel type"],
      ...(vesselNumberOptional ? [] : [["vessel_number", "Vessel number"]]),
      ["test_pressure", "Test pressure"],
      ["test_medium", "Test medium"],
      ["test_person_id", "Test person"],
      ["test_date", "Test date"],
      ...(isFreezerAssembly
        ? []
        : [["welder1_id", "Welder 1"], ["welder2_id", "Welder 2"]]),
    ];
    for (const [k, label] of required) {
      if (form[k] === "" || form[k] === null || form[k] === undefined) {
        return `${label} is required.`;
      }
    }
    if (Number(form.vessel_number) < 0) {
      return "Vessel number cannot be negative.";
    }
    return "";
  };

  const buildPayload = () => {
    const payload = {
      ...form,
      vessel_number:
        form.vessel_number === "" || form.vessel_number === null
          ? null
          : Number(form.vessel_number),
      witness_tested_by_nobo: !!form.witness_tested_by_nobo,
    };
    ["test_result", "blown_out_employee_id", "blown_out_date"].forEach((k) => {
      if (payload[k] === "") payload[k] = null;
    });
    return payload;
  };

  // Fail-safe pre-save for the photo workflow. Ensures the record exists and
  // returns its id. Throws (with a clear message) if required fields are missing
  // or the save fails — keeping the user on the same entry screen.
  const handleRequireSave = async () => {
    if (effectiveId) return effectiveId;
    setError("");
    const errMsg = validateForm();
    if (errMsg) {
      setError(errMsg);
      throw new Error(`Complete required fields first — ${errMsg}`);
    }
    const res = await api.post("/tests", buildPayload());
    const newId = res.data?.id;
    if (!newId) throw new Error("Could not save the record.");
    setSavedId(newId);
    // Reflect the new id in the URL without remounting the form/card.
    try {
      window.history.replaceState(null, "", `/tests/${newId}`);
    } catch (e) {
      console.debug("history.replaceState failed (non-critical)", e);
    }
    toast.success("Record saved — attaching photo…");
    return newId;
  };

  // ---- Record locking via "Blown out by" ----
  // Selecting a Blown-out-by employee does NOT save immediately. It first validates
  // all required fields + a conclusive Test Result, optimistically shows the choice,
  // and opens a confirm modal. Only "Confirm & Lock" persists it (and locks the record).
  const requestBlownOutBy = (employeeId) => {
    setError("");
    const errMsg = validateForm();
    if (errMsg) {
      toast.error(`Complete required fields first — ${errMsg}`);
      return;
    }
    const result = (form.test_result || "").trim().toLowerCase();
    if (!["pass", "fail", "uncertain"].includes(result)) {
      toast.error("Set a Test Result (Pass, Fail or Uncertain) before locking the record.");
      return;
    }
    // Optimistic display in the select (NOT saved until the user confirms).
    setForm((f) => ({ ...f, blown_out_employee_id: employeeId }));
    const date = form.blown_out_date || todayIso();
    setPendingLock({ employeeId, date, assumed: !form.blown_out_date });
  };

  const cancelLock = () => {
    setForm((f) => ({ ...f, blown_out_employee_id: "" }));
    setPendingLock(null);
  };

  const confirmLock = async () => {
    if (!pendingLock) return;
    const { employeeId, date } = pendingLock;
    if (isEdit) {
      try {
        await api.patch(`/tests/${id}`, {
          blown_out_employee_id: employeeId,
          blown_out_date: date,
        });
        setForm((f) => ({ ...f, blown_out_employee_id: employeeId, blown_out_date: date }));
        committed.current.blown_out_employee_id = employeeId;
        committed.current.blown_out_date = date;
        setLocked(true);
        setLockedMeta({ blown_out_date: date });
        toast.success("Record locked");
      } catch (err) {
        setForm((f) => ({ ...f, blown_out_employee_id: "" }));
        toast.error(formatApiError(err));
      }
    } else {
      // Create flow: record is created locked when the form is submitted.
      setForm((f) => ({ ...f, blown_out_employee_id: employeeId, blown_out_date: date }));
      toast.success("Record will be locked when created");
    }
    setPendingLock(null);
  };

  const blownOutName = pendingLock
    ? employees.find((e) => e.id === pendingLock.employeeId)?.name || "this employee"
    : "";

  // Admin / super-admin only — unlock a Blown Out record so it can be edited/deleted.
  // The blown-out employee + date are preserved in the notes (handled server-side).
  const unlockRecord = async () => {
    if (
      !window.confirm(
        "Unlock this record? It will become editable and deletable. The blown-out details will be preserved in the notes.",
      )
    )
      return;
    try {
      const res = await api.post(`/tests/${id}/unlock`);
      const d = res.data;
      setForm((f) => ({
        ...f,
        blown_out_employee_id: "",
        blown_out_date: "",
        notes: d.notes || "",
      }));
      committed.current.blown_out_employee_id = "";
      committed.current.blown_out_date = "";
      committed.current.notes = d.notes || "";
      setLocked(false);
      setLockedMeta(null);
      toast.success("Record unlocked");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // Memoised employee lists (helpers hoisted to module scope above). These run on
  // every keystroke without memoisation, so cache them on [employees] + selection.
  const activeWelders = useMemo(
    () => employees.filter((e) => roleIn(e, ["welder", "both"]) && notDeactivated(e)),
    [employees],
  );
  const activeTesters = useMemo(
    () => employees.filter((e) => roleIn(e, ["tester", "both"]) && notDeactivated(e)),
    [employees],
  );
  const testers = useMemo(
    () => withSelected(activeTesters, form.test_person_id, employees),
    [activeTesters, form.test_person_id, employees],
  );
  // Separate arrays so each welder slot keeps ITS saved employee selectable.
  const welders1 = useMemo(
    () => withSelected(activeWelders, form.welder1_id, employees),
    [activeWelders, form.welder1_id, employees],
  );
  const welders2 = useMemo(
    () => withSelected(activeWelders, form.welder2_id, employees),
    [activeWelders, form.welder2_id, employees],
  );

  return (
    <div className="p-6 md:p-10 max-w-5xl">
      <Link
        to="/tests"
        data-testid="back-to-tests"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-[#50493b] hover:text-[#39678f] mb-6"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Back to registry
      </Link>

      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#39678f] subtitle mb-2">
          {locked ? "Locked Record" : isEdit ? "Edit Record" : cloneFrom ? "Re-test" : "New Record"}
        </div>
        <h1 className="font-display text-5xl md:text-6xl tracking-tight text-[#33424f] leading-none">
          {locked ? "PRESSURE TEST · LOCKED" : isEdit ? "UPDATE PRESSURE TEST" : cloneFrom ? "LOG A RE-TEST" : "LOG A PRESSURE TEST"}
        </h1>
        {cloneFrom && !isEdit && (
          <p className="font-body text-sm text-[#50493b] mt-3 max-w-2xl">
            Pre-filled from the locked original. Complete the new test fields below — this will
            be stored as an independent record and linked via the notes field.
          </p>
        )}
      </div>

      {locked && (
        <div
          data-testid="locked-banner"
          className="mb-6 border border-[#33424f] bg-[#33424f] text-white p-5 flex items-start gap-4"
        >
          <Lock className="w-5 h-5 mt-0.5 shrink-0 text-[#ccb795]" />
          <div className="flex-1">
            <div className="subtitle uppercase tracking-[0.2em] text-[10px] text-[#ccb795] mb-1">
              Record locked · Blown Out{lockedMeta?.blown_out_date ? ` on ${lockedMeta.blown_out_date}` : ""}
            </div>
            <div className="font-body text-sm leading-relaxed">
              The testing medium has been blown out — this record is the final audit artefact
              and cannot be edited or deleted. For a re-test after repair or replacement, log a
              new record with the vessel details pre-filled.
            </div>
          </div>
          <div className="flex flex-col gap-2 shrink-0">
            {isAdmin && (
              <Button
                type="button"
                data-testid="unlock-from-form-btn"
                onClick={unlockRecord}
                className="bg-[#ccb795] hover:bg-[#b8a47f] text-[#33424f] subtitle uppercase tracking-wider text-xs h-10"
              >
                <Unlock className="w-4 h-4 mr-2" /> Unlock record
              </Button>
            )}
            <Button
              type="button"
              data-testid="retest-from-form-btn"
              onClick={() => navigate(`/tests/new?clone_from=${id}`)}
              className="bg-[#39678f] hover:bg-[#2c5272] text-white subtitle uppercase tracking-wider text-xs h-10"
            >
              <Copy className="w-4 h-4 mr-2" /> Log re-test
            </Button>
          </div>
        </div>
      )}

      <form onSubmit={onSubmit} className={`space-y-0 bg-white border border-[#33424f]/20 ${locked ? "opacity-80 pointer-events-none select-text" : ""}`} aria-disabled={locked}>
        <JobVesselSection
          form={form}
          update={update}
          orders={orders}
          vesselNumberOptional={vesselNumberOptional}
          fp={fp}
        />

        <TestParamsSection
          form={form}
          update={update}
          customPressure={customPressure}
          setCustomPressure={setCustomPressure}
          testers={testers}
          fp={fp}
        />

        <WeldersSection
          form={form}
          update={update}
          welders1={welders1}
          welders2={welders2}
          employees={employees}
          fp={fp}
          onBlownOutByChange={requestBlownOutBy}
        />

        <FailurePointsSection form={form} toggleFailurePoint={toggleFailurePoint} fp={fp} />

        <NotesSection form={form} update={update} fp={fp} />

        {error && (
          <div data-testid="form-error" className="mx-6 mb-6 p-3 border border-[#b30035] bg-[#fbe4ea] text-[#7a0024] text-sm font-body">
            {error}
          </div>
        )}

        <div className="flex items-center justify-between border-t border-[#33424f]/20 bg-[#f5f1ec] px-6 py-4">
          {isEdit ? (
            <>
              <span data-testid="autosave-hint" className="text-xs text-[#50493b] font-body">
                {locked
                  ? "This record is locked and cannot be edited."
                  : "Changes save automatically as you edit each field."}
              </span>
              <Link
                to="/tests"
                data-testid="done-editing-btn"
                className="inline-flex items-center bg-[#39678f] hover:bg-[#2c5272] text-white subtitle uppercase tracking-wider text-xs h-11 px-6"
              >
                Done
              </Link>
            </>
          ) : (
            <>
              <Link to="/tests" className="text-xs uppercase tracking-wider text-[#50493b] hover:text-[#33424f] subtitle">
                Cancel
              </Link>
              <Button
                data-testid="save-test-btn"
                type="submit"
                disabled={submitting || !canWrite}
                className="bg-[#39678f] hover:bg-[#2c5272] text-white subtitle uppercase tracking-wider text-xs h-11 px-6 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-4 h-4 mr-2" />
                {submitting ? "Saving…" : "Create test record"}
              </Button>
            </>
          )}
        </div>
        {isEdit && !isEmployee && <AuditLog entityId={id} />}
      </form>

      {/* Evidence photos — shown at the bottom of the test record page */}
      <PhotosSection
        testRecordId={effectiveId || null}
        locked={locked}
        onRequireSave={handleRequireSave}
      />

      {/* Lock confirmation — triggered by selecting a "Blown out by" employee */}
      <AlertDialog
        open={!!pendingLock}
        onOpenChange={(o) => {
          if (!o) cancelLock();
        }}
      >
        <AlertDialogContent data-testid="lock-confirm-dialog" className="border border-[#33424f]/30">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl tracking-tight text-[#33424f]">
              Confirm &amp; lock record
            </AlertDialogTitle>
            <AlertDialogDescription className="font-body text-sm text-[#50493b] leading-relaxed">
              Confirming final air blow out by{" "}
              <span className="font-semibold text-[#33424f]">{blownOutName}</span> done on{" "}
              <span className="font-semibold text-[#33424f]">{pendingLock?.date}</span>
              {pendingLock?.assumed ? " (assumed)" : ""}. Do you want to confirm and lock this record?
              Once locked it becomes the final audit record and can no longer be edited.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="lock-cancel-btn"
              className="subtitle uppercase tracking-wider text-xs"
            >
              Continue Editing
            </AlertDialogCancel>
            <Button
              data-testid="lock-confirm-btn"
              onClick={confirmLock}
              className="bg-[#4B4B9D] hover:bg-[#3d3d82] text-white subtitle uppercase tracking-wider text-xs"
            >
              <Lock className="w-4 h-4 mr-2" /> Confirm &amp; Lock Record
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

